import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Timer, ArrowLeft } from 'lucide-react';
import { generateQuestion, Question } from '../utils/gameLogic';
import { cn } from '../lib/utils';
import HowToPlay from './HowToPlay';
import FeverOverlay from './FeverOverlay';
import GameModal from './GameModal';

import { audioEngine } from '../utils/audioEngine';
import { PrismBuffs } from '../utils/prismSystem';

interface GameProps {
  onGameOver: (stats: { 
    score: number; 
    correctAnswers: number; 
    mistakes: number; 
    feverAnswers: number;
    feverTriggerCount: number;
  }) => void;
  onBack: () => void;
  onUpdateMission?: (id: string, amount?: number) => void;
  prismBuffs: PrismBuffs;
  tutorialStep?: number;
  onNextTutorialStep?: () => void;
}

const TUTORIAL_QUESTIONS: Question[] = [
  { id: 't1', left: '2', right: '5', leftVal: 2, rightVal: 5, points: 20 },
  { id: 't2', left: '8', right: '3', leftVal: 8, rightVal: 3, points: 20 },
  { id: 't3', left: '4', right: '9', leftVal: 4, rightVal: 9, points: 20 },
  { id: 't4', left: '6', right: '1', leftVal: 6, rightVal: 1, points: 20 },
  { id: 't5', left: '7', right: '8', leftVal: 7, rightVal: 8, points: 20 },
  { id: 't6', left: '9', right: '2', leftVal: 9, rightVal: 2, points: 20 },
  { id: 't7', left: '5', right: '6', leftVal: 5, rightVal: 6, points: 20 },
  { id: 't8', left: '1', right: '4', leftVal: 1, rightVal: 4, points: 20 },
  { id: 't9', left: '8', right: '5', leftVal: 8, rightVal: 5, points: 20 },
  { id: 't10', left: '3', right: '7', leftVal: 3, rightVal: 7, points: 20 },
  { id: 't11', left: '4', right: '2', leftVal: 4, rightVal: 2, points: 20 },
  { id: 't12', left: '9', right: '6', leftVal: 9, rightVal: 6, points: 20 },
  { id: 't13', left: '5', right: '8', leftVal: 5, rightVal: 8, points: 20 },
  { id: 't14', left: '2', right: '7', leftVal: 2, rightVal: 7, points: 20 },
  { id: 't15', left: '6', right: '3', leftVal: 6, rightVal: 3, points: 20 },
  { id: 't16', left: '1', right: '5', leftVal: 1, rightVal: 5, points: 20 },
  { id: 't17', left: '8', right: '4', leftVal: 8, rightVal: 4, points: 20 },
  { id: 't18', left: '7', right: '9', leftVal: 7, rightVal: 9, points: 20 },
  { id: 't19', left: '3', right: '1', leftVal: 3, rightVal: 1, points: 20 },
  { id: 't20', left: '4', right: '8', leftVal: 4, rightVal: 8, points: 20 },
];

// FIX 1: Pre-compute cloud layout as a module constant so the array is never
// recreated on re-renders. Each cloud only translates on X (no scale),
// keeping animations GPU-composited via transform only.
const CLOUDS = [
  { w: 120, h: 40,  top: '10%', left: '0%',  dur: 8  },
  { w: 160, h: 55,  top: '25%', left: '30%', dur: 10 },
  { w: 200, h: 70,  top: '40%', left: '60%', dur: 12 },
  { w: 240, h: 85,  top: '55%', left: '20%', dur: 14 },
  { w: 280, h: 100, top: '70%', left: '50%', dur: 16 },
  { w: 320, h: 115, top: '85%', left: '80%', dur: 18 },
];

// FIX 2: Pre-compute shatter rotation values so Math.random() is never called
// inside animate (which causes Framer Motion to receive a new target every render).
const SHATTER_ROTATIONS = [
  -540, 360, -720, 480, -360, 720, -480, 540
] as const;

export default function Game({ onGameOver, onBack, onUpdateMission, prismBuffs, tutorialStep, onNextTutorialStep }: GameProps) {
  const isTutorialActive = tutorialStep !== undefined && tutorialStep >= 23 && tutorialStep <= 28;
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60 + (prismBuffs?.extraTime || 0));

  const getQuestion = useCallback((num: number) => {
    if (isTutorialActive && num <= TUTORIAL_QUESTIONS.length) {
      return TUTORIAL_QUESTIONS[num - 1];
    }
    return generateQuestion(num);
  }, [isTutorialActive]);

  const [QuestionQueue, setQuestionQueue] = useState<Question[]>(() => [
    getQuestion(1),
    getQuestion(2),
    getQuestion(3)
  ]);
  const [QuestionNumber, setQuestionNumber] = useState(1);
  const [consecutiveCorrect, setConsecutiveCorrect] = useState(0);
  const [feverGauge, setFeverGauge] = useState(0);
  const [feverTimeLeft, setFeverTimeLeft] = useState(0);
  const [isFever, setIsFever] = useState(false);
  const [lastAnswerTime, setLastAnswerTime] = useState(Date.now());
  const [feedback, setFeedback] = useState<{ side: 'left' | 'right', type: 'correct' | 'wrong' } | null>(null);
  const [shatterParticles, setShatterParticles] = useState<{ id: string, x: number, y: number, value: string | number, side: 'left' | 'right', rot: number }[]>([]);
  const [startSequence, setStartSequence] = useState<'ready' | 'go' | null>(() => isTutorialActive ? null : 'ready');
  const [showCombo, setShowCombo] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [feverAnswers, setFeverAnswers] = useState(0);
  const [feverTriggerCount, setFeverTriggerCount] = useState(0);

  const currentQuestion = QuestionQueue[0];

  useEffect(() => {
    if ((tutorialStep === 26 || tutorialStep === 27) && !isFever) {
      setIsFever(true);
      setFeverTimeLeft(10);
      setFeverGauge(10);
      audioEngine.playFeverStart('pemdas');
    }
  }, [tutorialStep, isFever]);

  // Combo auto-hide timer
  useEffect(() => {
    if (showCombo) {
      const t = setTimeout(() => setShowCombo(false), 1000);
      return () => clearTimeout(t);
    }
  }, [showCombo, consecutiveCorrect]);

  // Start background music when gameplay starts (after countdown)
  useEffect(() => {
    if (showHowTo || startSequence !== null) return;
    audioEngine.startMusic(isFever ? 'pemdas_fever' : 'pemdas');
    // Music routing handled by App.tsx
  }, [showHowTo, startSequence, isFever]);

  // Play fever SFX when fever activates
  const prevFever = useRef(false);
  useEffect(() => {
    if (isFever && !prevFever.current) {
      audioEngine.playFever();
    }
    prevFever.current = isFever;
  }, [isFever]);

  useEffect(() => {
    if (showHowTo) return;
    if (startSequence === 'ready') {
      audioEngine.playSound('ready');
      const t = setTimeout(() => setStartSequence('go'), 1000);
      return () => clearTimeout(t);
    } else if (startSequence === 'go') {
      audioEngine.playSound('go');
      const t = setTimeout(() => {
        setStartSequence(null);
        setLastAnswerTime(Date.now());
      }, 1000);
      return () => clearTimeout(t);
    }
  }, [startSequence, showHowTo]);

  // FIX 5: All stats included in deps so the callback is never stale.
  const handleGameOver = useCallback(() => {
    onGameOver({
      score,
      correctAnswers,
      mistakes,
      feverAnswers,
      feverTriggerCount,
    });
  }, [score, correctAnswers, mistakes, feverAnswers, feverTriggerCount, onGameOver]);

  useEffect(() => {
    if (timeLeft === 0) {
      handleGameOver();
    }
  }, [timeLeft, handleGameOver]);

  // Regular Timer - Runs continuously and is not frozen by Fever Mode
  useEffect(() => {
    if (startSequence || showHowTo || timeLeft === 0 || isTutorialActive) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [startSequence, showHowTo, timeLeft, isTutorialActive]);

  // Fever Timer - Just handles countdown
  useEffect(() => {
    if (!isFever || startSequence || showHowTo || isTutorialActive) return;

    const interval = setInterval(() => {
      setFeverTimeLeft((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [isFever, startSequence, showHowTo, isTutorialActive]);

  // Fever End Handler
  useEffect(() => {
    if (isFever && feverTimeLeft === 0) {
      setIsFever(false);
      setFeverGauge(0);
      setConsecutiveCorrect(0);
    }
  }, [feverTimeLeft, isFever]);

  const handleAnswer = (side: 'left' | 'right') => {
    if (startSequence || feedback) return;
    
    const isCorrect = side === 'left' ? currentQuestion.leftVal > currentQuestion.rightVal : currentQuestion.rightVal > currentQuestion.leftVal;
    const now = Date.now();

    if (isCorrect) {
      audioEngine.playCorrect(feverGauge, 'pemdas');

      // FIX 2: Use pre-computed rotation values per particle index.
      const particles = Array.from({ length: 8 }).map((_, i) => ({
        id: Math.random().toString(36).substr(2, 9),
        x: (Math.random() - 0.5) * 400,
        y: (Math.random() - 0.5) * 400,
        value: side === 'left' ? currentQuestion.left : currentQuestion.right,
        side,
        rot: SHATTER_ROTATIONS[i],
      }));
      setShatterParticles(prev => [...prev, ...particles]);
      setTimeout(() => {
        setShatterParticles(prev => prev.filter(p => !particles.some(part => part.id === p.id)));
      }, 1000);

      setShowCombo(true);
      
      let points = isFever ? Math.round(100 * (1 + (prismBuffs?.feverScoreMultiplier || 0))) : 50;

      const newConsecutive = consecutiveCorrect + 1;
      setConsecutiveCorrect(newConsecutive);
      if (onUpdateMission) {
        onUpdateMission('math_god', 1);
        if (newConsecutive >= 10) {
          onUpdateMission('streak_starter', 1);
        }
      }

      setScore(prev => prev + points);
      setCorrectAnswers(prev => prev + 1);
      if (isFever) {
        const next = feverAnswers + 1;
        setFeverAnswers(next);
        if (tutorialStep === 27 && next >= 2) {
           onBack();
           onNextTutorialStep?.();
           return;
        }
      }

      if (!isFever) {
        const newGauge = Math.min(10, feverGauge + 1);
        setFeverGauge(newGauge);
        
        if (tutorialStep === 25 && newGauge >= 10) {
           onNextTutorialStep?.();
           setIsFever(true);
           setFeverTriggerCount(prev => prev + 1);
           setFeverTimeLeft(10);
           const nextNum = QuestionNumber + 1;
           setQuestionNumber(nextNum);
           setQuestionQueue(prev => {
             const nextQ = [...prev.slice(1)];
             nextQ.push(getQuestion(nextNum + 2));
             return nextQ;
           });
           return;
        }

        if (newGauge >= 10) {
          setIsFever(true);
          audioEngine.playFeverStart('pemdas');
          setFeverTriggerCount(prev => prev + 1);
          setFeverTimeLeft(10 + (prismBuffs?.extraFever || 0));
          setFeverGauge(10);
        }
      }

      setLastAnswerTime(now);

      // Advance to next Question
      const nextNum = QuestionNumber + 1;
      setQuestionNumber(nextNum);
      setQuestionQueue(prev => {
        const nextQ = [...prev.slice(1)];
        nextQ.push(getQuestion(nextNum + 2));
        return nextQ;
      });

    } else {
      audioEngine.playWrong();
      setFeedback({ side, type: 'wrong' });
      setMistakes(prev => prev + 1);
      setConsecutiveCorrect(0);

      if (isFever) {
        setIsFever(false);
        setFeverGauge(0);
        setFeverTimeLeft(0);
      }
      
      setLastAnswerTime(now);
      
      setTimeout(() => {
        setFeedback(null);
        setQuestionNumber(1);
        setQuestionQueue([
          getQuestion(1),
          getQuestion(2),
          getQuestion(3)
        ]);
      }, 400);
    }
  };

  return (
    <div 
      className={cn(
        "flex flex-col h-full transition-all duration-700 p-4 select-none relative overflow-hidden",
        isFever ? "bg-gradient-to-t from-amber-400 to-rose-300" : "bg-gradient-to-t from-sky-400 to-sky-100"
      )}
    >
      {/* Background Sky Pattern */}
      <div className="absolute inset-0 opacity-[0.2] pointer-events-none mix-blend-overlay" style={{ backgroundImage: 'radial-gradient(#ffffff 2px, transparent 2px)', backgroundSize: '30px 30px' }} />
      
      {/* FIX 1: Clouds only animate translateX (no scale). translateX is handled
          by the GPU compositor without layout recalc. Each cloud has its own
          CSS animation duration baked in via inline style. */}
      <div className="absolute inset-0 pointer-events-none opacity-40">
        {CLOUDS.map((c, i) => (
          <motion.div
            key={i}
            className="absolute bg-white rounded-full blur-[2px] shadow-[inset_0_-8px_12px_rgba(0,0,0,0.05),0_10px_20px_rgba(0,0,0,0.05)]"
            style={{ width: c.w, height: c.h, top: c.top, left: c.left }}
            animate={{ x: [-20, 20, -20] }}
            transition={{ duration: c.dur, repeat: Infinity, ease: 'easeInOut' }}
          />
        ))}
      </div>
      
      <AnimatePresence>
        {showHowTo && (
          <HowToPlay mode="duel" onClose={() => setShowHowTo(false)} />
        )}
      </AnimatePresence>

      <GameModal
        isOpen={showExitConfirm}
        onClose={() => setShowExitConfirm(false)}
        onConfirm={onBack}
        type="confirm"
        title="QUIT GAME?"
        message="Your heart will not be refunded and your current progress will be lost. Are you sure?"
        confirmLabel="QUIT"
        cancelLabel="STAY"
      />

      {/* Start Sequence Overlay */}
      <AnimatePresence>
        {startSequence && !showHowTo && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 2 }}
            className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/80 backdrop-blur-sm"
          >
            <motion.div
              key={startSequence}
              initial={{ scale: 0.5, rotate: -10 }}
              animate={{ scale: 1, rotate: 0 }}
              className="text-7xl font-num text-yellow-500 drop-shadow-[0_10px_0_rgba(0,0,0,0.1)]"
            >
              {startSequence === 'ready' ? 'READY?' : 'GO!'}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <FeverOverlay isActive={isFever} />

      {/* Utility Field Container */}
      <div className="bg-white shadow-[0_10px_30px_rgba(14,165,233,0.3)] rounded-[32px] p-6 mb-4 border-b-8 border-slate-200 relative z-10 overflow-hidden ring-4 ring-white/50">
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none mix-blend-multiply" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '6px 6px' }} />
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <button 
            onClick={() => {
              if (isTutorialActive) {
                onBack();
              } else {
                setShowExitConfirm(true);
              }
            }} 
            className="p-2 bg-[#fef3c7] text-[#d97706] rounded-xl border-b-[3px] border-[#f59e0b] shadow-sm active:translate-y-[3px] active:border-b-0 transition-all relative z-10"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={3} />
          </button>
          <div className="flex flex-col items-center">
            <motion.span 
              key={score}
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="text-4xl font-num text-[#0ea5e9] drop-shadow-sm relative z-10"
            >
              {score}
            </motion.span>
          </div>
          <div className="w-10" />
        </div>

        {/* Timer Section Row */}
        <div className="mb-4 relative z-10">
          <div className="flex justify-between items-end mb-2 h-10">
            <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-100 shadow-inner">
              <Timer className={cn("w-4 h-4 transition-colors duration-500", timeLeft <= 10 ? "text-rose-500 animate-pulse" : (timeLeft <= 30 ? "text-yellow-500" : "text-emerald-500"))} />
              <span className={cn("text-xl font-num tabular-nums font-black transition-colors duration-500", timeLeft <= 10 ? "text-rose-500" : "text-slate-700")}>
                {timeLeft}s
              </span>
            </div>
            <AnimatePresence>
              {isFever && (
                <motion.div 
                  initial={{ opacity: 0, x: 20, scale: 0.8 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: 20, scale: 0.8 }}
                  className="text-2xl font-num text-[#db2777] drop-shadow-sm"
                >
                  OVERLOAD!
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div className="h-4 bg-slate-100 rounded-full overflow-hidden border-none shadow-inner p-0.5">
            <motion.div
              className={cn("h-full rounded-full transition-colors duration-500", isFever ? "bg-purple-500" : (timeLeft <= 10 ? "bg-rose-500" : (timeLeft <= 30 ? "bg-yellow-400" : "bg-emerald-400")))}
              initial={{ width: '100%' }}
              animate={{ width: isFever ? `${(feverTimeLeft / (10 + (prismBuffs?.extraFever || 0))) * 100}%` : `${(timeLeft / (60 + (prismBuffs?.extraTime || 0))) * 100}%` }}
              transition={{ type: 'tween', ease: 'linear', duration: 1 }}
            />
          </div>
        </div>

        {/* FIX 3: Fever Gauge — during fever, replaced the 10 individual
            Framer Motion JS-animated backgroundColor divs with a single CSS
            class. JS backgroundColor animation bypasses the GPU and runs on
            the main thread, causing jank when all 10 fire at once. */}
        <div className="transition-all duration-300 relative z-10">
          <div className="flex justify-between text-[8px] font-black mb-2 uppercase tracking-[0.2em] transition-colors duration-300">
            <span className={cn(isFever ? "text-[#fbbf24] animate-pulse" : "text-slate-500")}>
              {isFever ? "SONIC OVERLOAD" : "FEVER ENERGY"}
            </span>
            <span className={cn(isFever ? "text-[#fbbf24]" : "text-slate-500")}>
              {isFever ? "ACTIVE" : `${feverGauge}/10`}
            </span>
          </div>
          <div className={cn(
            "flex gap-1 h-6 p-1.5 rounded-xl transition-all duration-500 border-none shadow-inner bg-slate-100",
            isFever && "shadow-[0_0_20px_rgba(250,204,21,0.3)]"
          )}>
            {Array.from({ length: 10 }).map((_, i) => (
              <div
                key={i}
                className={cn(
                  "flex-1 rounded-sm transition-colors duration-200",
                  isFever
                    ? "bg-yellow-400 animate-fever-pip"
                    : i < feverGauge ? "bg-[#db2777]" : "bg-white"
                )}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Question Area */}
      <div className="flex-1 flex flex-col justify-center items-center relative z-10 w-full">
        {/* Shatter Particles — FIX 2: rotate uses pre-computed value, not Math.random() */}
        <AnimatePresence>
          {shatterParticles.map(p => (
            <motion.div
              key={p.id}
              initial={{ scale: 1, opacity: 1, x: p.side === 'left' ? '25%' : '75%', y: '50%' }}
              animate={{ 
                x: p.side === 'left' ? `calc(25% + ${p.x}px)` : `calc(75% + ${p.x}px)`,
                y: `calc(50% + ${p.y}px)`,
                scale: 0,
                opacity: 0,
                rotate: p.rot,
              }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="absolute w-12 h-12 rounded-2xl bg-yellow-400 text-white flex items-center justify-center text-xl font-num z-[60] pointer-events-none clay-base border-none"
            >
              {p.value}
            </motion.div>
          ))}
        </AnimatePresence>

        <div className="relative flex justify-center items-center h-[380px] w-full max-w-[440px] mx-auto mt-12 mb-2">
          <AnimatePresence mode="popLayout">
            {QuestionQueue.slice(0, 4).map((q, idx) => {
              const isTop = idx === 0;
              const isLayer1 = idx === 1;
              const isLayer2 = idx === 2;
              const isLayer3 = idx === 3;
              const isWrong = isTop && feedback?.type === 'wrong';

              return (
                <motion.div 
                  id={isTop ? "tutorial-question-card" : undefined}
                  key={q.id}
                  initial={{ opacity: 0, scale: 0.82, y: -210 }}
                  animate={{ 
                    opacity: isTop ? 1 : (isLayer1 ? 0.85 : (isLayer2 ? 0.6 : 0.2)), 
                    y: isTop ? 0 : (isLayer1 ? -85 : (isLayer2 ? -155 : -210)), 
                    scale: isTop ? 1 : (isLayer1 ? 0.94 : (isLayer2 ? 0.88 : 0.82)),
                    zIndex: 10 - idx,
                  }}
                  exit={{ 
                    opacity: 0, 
                    scale: 1.1, 
                    y: -20,
                    transition: { duration: 0.08 }
                  }}
                  transition={{ type: 'spring', damping: 35, stiffness: 900 }}
                  className={cn(
                    "absolute w-full aspect-[3.5/1] rounded-full flex items-center shadow-2xl",
                    isTop ? "bg-white clay-base border-none" : "bg-white/90 border-none pointer-events-none shadow-md",
                    isWrong && "bg-rose-50"
                  )}
                >
                  <div className="flex items-center justify-between w-full px-12 h-full">
                    {/* Left Val */}
                    <div className={cn(
                      "flex-1 flex items-center justify-center text-4xl font-num transition-all",
                      isTop ? (feedback?.side === 'left' && feedback.type === 'wrong' ? "text-rose-500" : "text-sky-600") : "text-slate-400 font-bold"
                    )}>
                      {isLayer3 ? '' : q.left}
                    </div>

                    <div className={cn(
                      "mx-4 text-xl font-num transition-all",
                      isTop ? "text-slate-200" : "text-slate-100"
                    )}>VS</div>

                    {/* Right Val */}
                    <div className={cn(
                      "flex-1 flex items-center justify-center text-4xl font-num transition-all",
                      isTop ? (feedback?.side === 'right' && feedback.type === 'wrong' ? "text-rose-500" : "text-sky-600") : "text-slate-400 font-bold"
                    )}>
                      {isLayer3 ? '' : q.right}
                    </div>
                  </div>

                  {isTop && (
                    <div className="absolute inset-0 flex pointer-events-none">
                      <button 
                        onClick={() => handleAnswer('left')}
                        className="flex-1 h-full cursor-pointer pointer-events-auto rounded-l-full"
                        aria-label="Left side greater"
                      />
                      <button 
                        onClick={() => handleAnswer('right')}
                        className="flex-1 h-full cursor-pointer pointer-events-auto rounded-r-full"
                        aria-label="Right side greater"
                      />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Action Buttons */}
        <div className="w-full max-w-sm flex items-center justify-between pointer-events-auto z-20 mt-12 px-4 relative">
          <button
            id="tutorial-left-btn"
            onClick={() => handleAnswer('left')}
            className={cn(
              "group w-24 h-24 rounded-full flex items-center justify-center shadow-xl transition-all border-b-[6px] active:border-b-[0px] active:translate-y-[6px]",
              isFever 
                ? "bg-gradient-to-b from-rose-500 to-rose-600 border-rose-800 shadow-[0_10px_20px_rgba(244,63,94,0.4)]" 
                : "bg-gradient-to-b from-sky-400 to-sky-500 border-sky-600 shadow-[0_10px_20px_rgba(14,165,233,0.4)]"
            )}
          >
            <div className="w-8 h-8 rounded-full bg-white shadow-[0_2px_4px_rgba(0,0,0,0.1)] pointer-events-none mb-1" />
          </button>

          <button
            id="tutorial-right-btn"
            onClick={() => handleAnswer('right')}
            className={cn(
              "group w-24 h-24 rounded-full flex items-center justify-center shadow-xl transition-all border-b-[6px] active:border-b-[0px] active:translate-y-[6px] relative",
              isFever 
                ? "bg-gradient-to-b from-rose-500 to-rose-600 border-rose-800 shadow-[0_10px_20px_rgba(244,63,94,0.4)]" 
                : "bg-gradient-to-b from-sky-400 to-sky-500 border-sky-600 shadow-[0_10px_20px_rgba(14,165,233,0.4)]"
            )}
          >
            <div className="w-8 h-8 rounded-full bg-white shadow-[0_2px_4px_rgba(0,0,0,0.1)] pointer-events-none mb-1" />
            {tutorialStep === 25 && QuestionNumber === 1 && (
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ repeat: Infinity, duration: 0.8, ease: "easeInOut" }}
                className="absolute -top-12 left-1/2 -translate-x-1/2 text-4xl pointer-events-none select-none z-30"
              >
                ⬇️
              </motion.div>
            )}
          </button>
        </div>

        {/* Feedback Overlay */}
        <AnimatePresence>
          {feedback?.type === 'wrong' && (
            <motion.div
              initial={{ scale: 0, rotate: -45 }}
              animate={{ scale: 1.5, rotate: 0 }}
              exit={{ scale: 2, opacity: 0 }}
              className="absolute font-num text-8xl z-50 pointer-events-none drop-shadow-[0_8px_0_rgba(0,0,0,0.1)] text-[#f43f5e]"
            >
              MISS!
            </motion.div>
          )}
        </AnimatePresence>

        {/* Combo Counter */}
        <AnimatePresence>
          {showCombo && consecutiveCorrect > 1 && (
            <motion.div
              key={consecutiveCorrect}
              initial={{ scale: 0.5, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 1.5 }}
              transition={{ duration: 0.2 }}
              className="absolute top-[25%] left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none flex flex-col items-center"
            >
              <span className="text-8xl font-num text-yellow-500 drop-shadow-[0_4px_0_rgba(0,0,0,0.1)]">
                {consecutiveCorrect}
              </span>
              <span className="text-xl font-black text-slate-400 uppercase tracking-[0.2em]">COMBO!</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="absolute bottom-[40px] left-0 right-0 text-center text-white/80 font-black text-[10px] uppercase tracking-[0.3em] z-10 pointer-events-none drop-shadow-md">
        PICK THE GREATER SIDE!
      </div>
    </div>
  );
}
